import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmployeeTable = ({ employees, onRemove, onToggleStatus }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredEmployees = employees?.filter(emp => {
    const matchesSearch = 
      emp?.firstName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      emp?.lastName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      emp?.employeeNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      emp?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      emp?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    if (filterStatus === 'active') return matchesSearch && emp?.isActive;
    if (filterStatus === 'inactive') return matchesSearch && !emp?.isActive;
    return matchesSearch;
  }) || [];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-4">
        <div className="flex gap-2">
          <Button
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('all')}
          >
            All ({employees?.length || 0})
          </Button>
          <Button
            variant={filterStatus === 'active' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('active')}
          >
            Active ({employees?.filter(e => e?.isActive)?.length || 0})
          </Button>
          <Button
            variant={filterStatus === 'inactive' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('inactive')}
          >
            Inactive ({employees?.filter(e => !e?.isActive)?.length || 0})
          </Button>
        </div>

        <div className="relative w-full md:w-64">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Employee #</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Name</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Email</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Department</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Position</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Employment Type</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Citizenship</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Hire Date</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees?.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-8 text-muted-foreground">
                  No employees found
                </td>
              </tr>
            ) : (
              filteredEmployees?.map((employee) => (
                <tr key={employee?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4 text-sm font-medium text-foreground">{employee?.employeeNumber}</td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {employee?.firstName} {employee?.lastName}
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">{employee?.email}</td>
                  <td className="py-3 px-4 text-sm text-foreground">{employee?.department}</td>
                  <td className="py-3 px-4 text-sm text-foreground">{employee?.position}</td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {employee?.employmentType?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'N/A'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-foreground">
                      {employee?.citizenStatus?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'N/A'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">{formatDate(employee?.hireDate)}</td>
                  <td className="py-3 px-4">
                    {employee?.isActive ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                        <Icon name="CheckCircle" size={12} className="mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        <Icon name="XCircle" size={12} className="mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => onToggleStatus?.(employee?.id, employee?.isActive)}
                        className="text-primary hover:text-primary/80 transition-colors"
                        title={employee?.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <Icon name={employee?.isActive ? 'UserX' : 'UserCheck'} size={16} />
                      </button>
                      <button
                        onClick={() => onRemove?.(employee?.id)}
                        className="text-error hover:text-error/80 transition-colors"
                        title="Remove Employee"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeeTable;
